/**
 * MiscShowcase — Media / 기타 컴포넌트 카탈로그 페이지.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import { useEffect, useRef, useState } from 'react'
import {
  Card,
  Avatar,
  Badge,
  Tag,
  Progress,
  Spin,
  Skeleton,
  Carousel,
  Button,
  Space,
  FloatButton,
  Segmented,
  Switch,
  Empty,
  Typography
} from 'antd'
import {
  UserOutlined,
  BellOutlined,
  MailOutlined,
  CheckOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  CopyOutlined,
  ArrowUpOutlined,
  PlusOutlined
} from '@ant-design/icons'
import { useMessage } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title } = Typography

/**
 * ============================================================================
 * MiscShowcase — Media / 기타 컴포넌트 카탈로그
 * ============================================================================
 *
 * 【역할】 단일 카테고리에 묶기 어려운 medium-priority variant 모음.
 *         Avatar/Badge/Tag/Progress/Spin/Skeleton/Carousel/Segmented/Switch
 *         /Empty/Typography/FloatButton 을 4×2 그리드 + 하단 1행으로 시연.
 *
 * 【시연 항목】
 *   - Avatar — 기본 / Group
 *   - Badge — count + 색상 + 아이콘 wrap
 *   - Tag — 색상/icon/closable
 *   - Segmented + Switch (제어/loading)
 *   - Progress + Spin
 *   - Skeleton (avatar + paragraph)
 *   - BlockUI — Spin 으로 영역 차단 토글 (fw-block-spin wrapperClassName)
 *   - Carousel — autoplay + ResizeObserver 로 슬라이드 높이 동기화
 *   - 하단: Typography variant + Empty + FloatButton.Group(BackTop 포함)
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/misc' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd 12+ 컴포넌트, ResizeObserver, useMessage(framework)
 * 【참고】 https://ant.design/components/avatar
 */
export default function MiscShowcase() {
  const msg = useMessage()
  const { t } = useLabel()
  // ─── 로컬 상태 ─
  // blocked: BlockUI(Spin spinning) 토글 상태 — true 시 영역 차단 오버레이 표시
  // seg: Segmented 선택 값 (Daily/Weekly/Monthly) — 가로 토글 그룹 제어값
  // sw: 첫 번째 Switch 제어 상태 — 다른 두 Switch 는 비제어/loading 데모
  // Why 개별 useState 분리: 각 컴포넌트 독립 상태 — 묶어서 reducer 까지 갈 필요 없음
  const [blocked, setBlocked] = useState(false)
  const [seg, setSeg] = useState<string | number>('Daily')
  const [sw, setSw] = useState(true)

  // Carousel 슬라이드 높이를 그리드 셀 높이에 동기화 — 카드 head/body padding/dots 제외값.
  // Why useRef: DOM 직접 측정이 필요 (clientHeight 는 ref 없이 접근 불가)
  // Why useState(slideHeight): re-render 트리거 — Carousel children 의 height 스타일 반영
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [slideHeight, setSlideHeight] = useState(200)
  useEffect(() => {
    // ─── ResizeObserver 로 카드 외곽 크기 추적 → slideHeight 동기화 ─
    // 라이프사이클: mount → 즉시 compute() → ResizeObserver attach → unmount 시 disconnect
    const el = cardRef.current
    if (!el) return
    const compute = () => {
      // 1) 카드 외곽 높이 기준 — 그리드 셀 1fr 에 의해 결정되므로 피드백 루프 없음
      const cardH = el.clientHeight
      // 2) Card head + body padding + dots 영역 제외해서 슬라이드 가용 높이 산출
      const CARD_HEAD = 38
      const BODY_PADDING = 12 // 6*2
      const DOTS = 24
      // 3) 최소 120px 보장 (작은 화면에서도 너무 찌그러지지 않도록)
      const h = Math.max(120, cardH - CARD_HEAD - BODY_PADDING - DOTS)
      setSlideHeight(h)
    }
    // 4) 초기 1회 + 리사이즈마다 compute
    compute()
    const ro = new ResizeObserver(compute)
    ro.observe(el)
    return () => ro.disconnect()
  }, []) // deps 비움 — observer 1회만 등록

  // ─── Carousel 슬라이드 데이터 — 4종 색상 원형 + 라벨 ─
  // 각 슬라이드는 동일 구조 (color/name/desc) — autoplay 로 순환
  // Why 인라인 배열: 컴포넌트 외부 상수화 시 t() 호출이 mount 전 실행되어 i18n 미적용
  // Why hex 색상 직접: 데모 시연용 — 실제 운영은 design token (theme.token.colorPrimary 등)
  const carouselItems = [
    { name: `${t('LBL_ITEM', '항목')} 1`, desc: t('LBL_MISC_FIRST', '첫 번째'), color: '#3B82F6' },
    { name: `${t('LBL_ITEM', '항목')} 2`, desc: t('LBL_MISC_SECOND', '두 번째'), color: '#10B981' },
    { name: `${t('LBL_ITEM', '항목')} 3`, desc: t('LBL_MISC_THIRD', '세 번째'), color: '#F59E0B' },
    { name: `${t('LBL_ITEM', '항목')} 4`, desc: t('LBL_MISC_FOURTH', '네 번째'), color: '#EF4444' }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_MISC_COMPONENTS', 'Media / Misc 컴포넌트')}
        </Title>
      </div>

      {/* ─── Grid: 4 col × 2 row 카탈로그 ─
          row1: Avatar / Badge / Tag / Segmented+Switch
          row2: Progress+Spin / Skeleton / BlockUI / Carousel */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gridTemplateRows: '1fr 1fr',
          gap: 10
        }}
      >
        {/* ─── [Cell 1] Avatar ─
            1) 텍스트 / 2) 배경색 / 3) icon / 4) Avatar.Group(중첩) */}
        <Card
          size="small"
          title="Avatar"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Space size="small" wrap align="center">
            <Avatar>A</Avatar>
            <Avatar style={{ background: '#3B82F6' }}>B</Avatar>
            <Avatar icon={<UserOutlined />} style={{ background: '#10B981' }} />
            <Avatar.Group>
              <Avatar style={{ background: '#3B82F6' }}>A</Avatar>
              <Avatar style={{ background: '#10B981' }}>B</Avatar>
              <Avatar style={{ background: '#F59E0B' }}>C</Avatar>
              <Avatar style={{ background: '#94A3B8' }}>+2</Avatar>
            </Avatar.Group>
          </Space>
        </Card>

        {/* ─── [Cell 2] Badge ─
            1) 숫자 count / 2) 색상 (green/orange/red) / 3) 문자열 count="!"
            4) wrap children — BellOutlined/MailOutlined 위에 카운트 표시 */}
        <Card
          size="small"
          title="Badge"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Space size="middle" wrap align="center">
            <Badge count={2} />
            <Badge count={8} color="green" />
            <Badge count={12} color="orange" />
            <Badge count="!" color="red" />
            <Badge count={3}>
              <BellOutlined style={{ fontSize: 20 }} />
            </Badge>
            <Badge count={5}>
              <MailOutlined style={{ fontSize: 20 }} />
            </Badge>
          </Space>
        </Card>

        {/* ─── [Cell 3] Tag ─
            preset 색상(success/processing/warning/error/magenta)
            + icon Tag (CheckOutlined) + closable Tag (e.preventDefault 로 닫힘 무효화) */}
        <Card
          size="small"
          title="Tag"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Space wrap size={[4, 6]}>
            <Tag color="success">Success</Tag>
            <Tag color="processing">Info</Tag>
            <Tag color="warning">Warn</Tag>
            <Tag color="error">Danger</Tag>
            <Tag color="magenta">Contrast</Tag>
            <Tag icon={<CheckOutlined />} color="success">
              {t('BTN_APPROVE', '승인')}
            </Tag>
            <Tag color="blue">React</Tag>
            <Tag color="green">Spring</Tag>
            <Tag closable onClose={(e) => e.preventDefault()}>
              {t('BTN_DELETE', '삭제')}
            </Tag>
          </Space>
        </Card>

        {/* ─── [Cell 4] Segmented + Switch ─
            Segmented: 3-option 가로 토글 (Daily/Weekly/Monthly)
            Switch 변형: 1) 제어 / 2) checkedChildren=ON / 3) loading 상태 */}
        <Card
          size="small"
          title="Segmented / Switch"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Segmented
              size="small"
              options={['Daily', 'Weekly', 'Monthly']}
              value={seg}
              onChange={setSeg}
            />
            <Space>
              <Switch checked={sw} onChange={setSw} />
              <Switch defaultChecked checkedChildren="ON" unCheckedChildren="OFF" />
              <Switch loading defaultChecked />
            </Space>
          </Space>
        </Card>

        {/* ─── [Cell 5] Progress + Spin ─
            Progress: 65%/40%(showInfo=false)/100%(status='active')
            Spin: 인라인 스피너 + 라벨 */}
        <Card
          size="small"
          title="Progress / Spin"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Progress percent={65} size="small" />
            <Progress percent={40} size="small" showInfo={false} />
            <Progress percent={100} size="small" status="active" />
            <Space>
              <Spin size="small" />
              <span style={{ color: '#666', fontSize: 12 }}>{t('MSG_LOADING', '로딩 중...')}</span>
            </Space>
          </Space>
        </Card>

        {/* ─── [Cell 6] Skeleton ─
            avatar + paragraph rows=2 + active(shimmer) — 로딩 placeholder */}
        <Card
          size="small"
          title="Skeleton"
          styles={{ body: { padding: 10, overflow: 'auto' } }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Skeleton avatar active paragraph={{ rows: 2 }} />
        </Card>

        {/* ─── [Cell 7] BlockUI ─
            Spin wrapperClassName="fw-block-spin" → 영역 차단 스타일 (글로벌 CSS)
            blocked 토글 → spinning 활성/비활성 (children 위 오버레이) */}
        <Card
          size="small"
          title="BlockUI (Spin wrapper)"
          styles={{
            body: {
              padding: 10,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }
          }}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Button
            size="small"
            onClick={() => setBlocked(!blocked)}
            style={{ marginBottom: 8, flex: '0 0 auto', alignSelf: 'flex-start' }}
          >
            {blocked ? t('BTN_UNBLOCK', '해제') : t('BTN_BLOCK', '블로킹')}
          </Button>
          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <Spin spinning={blocked} wrapperClassName="fw-block-spin">
              <div
                style={{
                  padding: 8,
                  border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                  borderRadius: 4,
                  fontSize: 12
                }}
              >
                <div>{t('LBL_BLOCK_DESC1', 'Spin 으로 차단 가능')}</div>
                <div>{t('LBL_BLOCK_DESC2', '버튼 클릭 시 상호작용 차단')}</div>
              </div>
            </Spin>
          </div>
        </Card>

        {/* ─── [Cell 8] Carousel ─
            autoplay + dots — 4개 슬라이드 자동 순환
            slideHeight 는 ResizeObserver(useEffect 위) 로 동적 산출 */}
        <div ref={cardRef} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Card
            size="small"
            title="Carousel"
            styles={{
              body: {
                padding: 6,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }
            }}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          >
            {/* Carousel(slick) 은 flex 컨테이너에서 width 계산이 깨지므로 block + 고정 폭 기반.
              슬라이드 높이는 카드 외곽 크기 기준으로 ResizeObserver 가 계산. */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
                borderRadius: 4,
                overflow: 'hidden'
              }}
            >
              <Carousel autoplay dots style={{ width: '100%' }}>
                {carouselItems.map((item) => (
                  <div key={item.name}>
                    <div
                      style={{
                        height: slideHeight,
                        padding: '16px 12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          background: item.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: 22
                        }}
                      >
                        ■
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{item.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, #666)' }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </Card>
        </div>
      </div>

      {/* Bottom row — Typography + Empty */}
      <div
        style={{
          flex: '0 0 auto',
          marginTop: 10,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10
        }}
      >
        <Card size="small" title="Typography" styles={{ body: { padding: 10 } }}>
          <Typography.Text strong>{t('LBL_TYPO_BOLD', '굵게')} </Typography.Text>
          <Typography.Text italic>{t('LBL_TYPO_ITALIC', '기울임')} </Typography.Text>
          <Typography.Text type="secondary">{t('LBL_TYPO_SECONDARY', '보조')} </Typography.Text>
          <Typography.Text keyboard>Ctrl+S</Typography.Text>
        </Card>
        <Card size="small" title="Empty / FloatButton" styles={{ body: { padding: 10 } }}>
          <Space align="center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Simple Empty" />
            <span style={{ fontSize: 12, color: '#666' }}>
              {t('LBL_FLOAT_BTN_HINT', '우측 하단 플로팅 버튼 확인 →')}
            </span>
          </Space>
        </Card>
      </div>

      {/* ─── FloatButton.Group — 우측 하단 고정 메뉴 ─
          trigger='click' + type='primary' + 4개 액션 + BackTop 포함
          - Refresh / Excel / Print / Copy → useMessage 토스트
          - BackTop: visibilityHeight=0 (즉시 표시) */}
      <FloatButton.Group
        trigger="click"
        type="primary"
        style={{ insetInlineEnd: 40 }}
        icon={<PlusOutlined />}
      >
        <FloatButton
          icon={<ReloadOutlined />}
          tooltip={t('BTN_REFRESH', '새로고침')}
          onClick={() => msg.info(t('MSG_REFRESH_RUN', '새로고침 실행'))}
        />
        <FloatButton
          icon={<FileExcelOutlined />}
          tooltip={t('BTN_EXCEL', '엑셀')}
          onClick={() => msg.info(t('MSG_EXCEL_DOWNLOAD_RUN', '엑셀 다운로드 실행'))}
        />
        <FloatButton
          icon={<PrinterOutlined />}
          tooltip={t('BTN_PRINT', '인쇄')}
          onClick={() => msg.info(t('MSG_PRINT_RUN', '인쇄 실행'))}
        />
        <FloatButton
          icon={<CopyOutlined />}
          tooltip={t('BTN_COPY', '복사')}
          onClick={() => msg.info(t('MSG_COPY_SELECTED', '선택행 복사'))}
        />
        <FloatButton.BackTop
          icon={<ArrowUpOutlined />}
          tooltip={t('BTN_TO_TOP', '맨위로')}
          visibilityHeight={0}
        />
      </FloatButton.Group>
    </PageContainer>
  )
}
